// Phase 15 — In-case interest calculator section.
//
// Renders inside both CaseView (kind='case') and CollectiveCaseView
// (kind='cc'). Multi-amount, official-rate driven (legal_interest /
// commercial_interest), with autosave to InterestCalculation.state and
// a "Generate memo" action that ships a docx blob to the case folder
// 04.MEMOS subfolder via the server's memo-emit endpoint.
//
// Stable-identity contract: defined at module scope (not inline in a
// render body) — see CLAUDE.md "Known patterns" section.

import React, { useEffect, useRef, useState, type CSSProperties } from "react"; // 212 §22 — `import React`: react/react-in-jsx-scope's own remedy (189 F.1)
import { Plus, Trash2, FileText, Copy } from "lucide-react";
import { api, computeInterest } from "../api";
import { useTranslation, tImperative } from "../i18n";
import { useOfficialRates, ratesByKind } from "../hooks/useOfficialRates";
import { fmtBE, fmtBEPercent, fmtBEDate } from "../lib/interestEngine";
// c1-rate-coverage-checkpoint (brief 30 §3) — the engine states a refusal's
// FACTS; each surface renders them. The cards are UI chrome in the reader's
// language (refusalUI) — and since the ten-channels slice (exit 26) the
// clipboard prose is too: "a French document" was true only because nobody had
// built the other languages, the same circularity unit C deleted in docGen,
// and the prose is a copy of the on-screen breakdown, which already renders in
// the reader's language. refusalFR keeps its one consumer (memoExport, the
// p17-9 oracle's half) and is deliberately not imported here.
import { refusalUI, type Translate } from "../lib/interestRefusal";
import { RAD, FS } from "../lib/tokens";
import MenuSelect from "../components/MenuSelect"; // 154B §2
import { InfoHint, AddButton, ListNotLoaded } from "../components/SharedUI"; // 176 §1.1b
import { useReadMark, usePageListContext, oneRow } from "../hooks/usePageList"; // 212 §22
import { ListLoadContext, PAGE_LISTS } from "../lib/listLoad";
import AmountField from "../components/AmountField"; // 208 §10 — the one amount field
import { languageRefusalAction } from "../lib/caseLanguage"; // 209 §5

const RATE_TYPES = [
  {
    v: "legal_interest",
    labelKey: "case.calculator.rate_type_legal",
    fallback: "Legal interest (annual)",
  },
  {
    v: "commercial_interest",
    labelKey: "case.calculator.rate_type_commercial",
    fallback: "Commercial interest (semi-annual)",
  },
];

// The refusal's rate label in the calculator's own registry — the UI and,
// since exit 26, the clipboard prose too (which fixes the defect the census
// named: RATE_TYPES' English fallback labels inside the French document).
const rateTypeLabelUI = (t: Translate) => (k: string | undefined) => {
  const rt = RATE_TYPES.find((x) => x.v === k);
  return rt ? t(rt.labelKey, { fallback: rt.fallback }) : String(k ?? "");
};

function uid() {
  return (
    "a" +
    Math.random().toString(36).slice(2, 10) +
    Date.now().toString(36).slice(-4)
  );
}

function todayISO() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

function toISO(v: any) {
  if (!v) return null;
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
}

function emptyAmount(seed: any = {}) {
  return {
    id: uid(),
    label: seed.label || "",
    principal: seed.principal ?? null,
    startDate: seed.startDate || null,
    endDate: seed.endDate || null,
    rateType: seed.rateType || "legal_interest",
    capitalizations: [],
    payments: [],
  };
}

const inputStyle: CSSProperties = {
  fontSize: FS.s11,
  padding: "3px 5px",
  minWidth: 0,
  width: "100%",
  boxSizing: "border-box",
};
const labelStyle: CSSProperties = {
  fontSize: FS.s9_5,
  color: "var(--tm)",
  display: "block",
  marginBottom: 2,
};
const colStyle: CSSProperties = {
  flex: "0 0 280px",
  minWidth: 260,
  maxWidth: 320,
  background: "var(--bi)",
  border: "1px solid var(--br)",
  borderRadius: RAD.r8,
  padding: 10,
  display: "flex",
  flexDirection: "column",
  gap: 8,
};
const sectionTitleStyle: CSSProperties = {
  fontSize: FS.s10_5,
  fontWeight: 600,
  color: "var(--ts)",
  textTransform: "uppercase",
  letterSpacing: ".3px",
  margin: "4px 0 2px",
};

export default function CaseCalculatorSection({
  kind,
  cs,
  canEdit,
  toast,
  hideTitle = false,
}: any) {
  const { t } = useTranslation();
  const { rates } = useOfficialRates();
  const [state, setState] = useState<any>(null); // { amounts, notes, asOfDate }
  const [autofill, setAutofill] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const saveTimer = useRef<any>(null);
  const lastSentRef = useRef<any>(null);
  /* 213 §11.5 — NO BLANK CALCULATION SAVED: a matter with none is shown a seeded blank one, and until 213 the
     autosave wrote it the first time someone who may edit opened the matter (212's census). The seed is marked;
     the autosave leaves it alone until a person changes something (`mutate`, every edit's one path) — the live
     autofill of amount[0] from the matter is not a person's change. */
  const blankRef = useRef(false);

  const parentId = cs?.id;
  const apiPath =
    kind === "cc"
      ? `/collective-cases/${parentId}/interest-calculation`
      : `/cases/${parentId}/interest-calculation`;

  // Initial load: GET state + autofill (claimAmount + introductionDate).
  // If no row yet, seed a single empty amount pre-filled from autofill.
  /* 212 §22 — asked the 210 §1 way (again, twice, a second apart, each failure
     on the console); still failing, the section says in its place that its
     read failed, with « Réessayer ». No blank calculation is seeded then: the
     autosave would write it over the stored one. */
  const { entry: calcRead, again: calcAgain, ask: askCalc } = useReadMark(PAGE_LISTS.calculator);
  const calcLists = usePageListContext(calcRead);
  useEffect(() => {
    if (!parentId) return;
    let alive = true;
    setLoading(true);
    void (askCalc("interest calculation", () => oneRow(api.get(apiPath))) as Promise<
      | [{
          autofill?: { claimAmount?: number | null; introductionDate?: string | null } | null;
          calculation?: {
            state?: { amounts?: unknown[]; notes?: string | null; asOfDate?: string | null } | null;
          } | null;
        } | null]
      | null
    >)
      .then((rows) => {
        if (!alive || !rows) return;
        const r = rows[0];
        setAutofill(r?.autofill || null);
        const stored = r?.calculation?.state;
        if (stored && Array.isArray(stored.amounts)) {
          setState({
            amounts: stored.amounts,
            notes: stored.notes || "",
            asOfDate: stored.asOfDate || null,
          });
          lastSentRef.current = JSON.stringify(stored);
          blankRef.current = false;
        } else {
          const seed = {
            label: t("case.calculator.principal", { fallback: "Principal" }),
            principal: r?.autofill?.claimAmount ?? null,
            startDate: toISO(r?.autofill?.introductionDate),
            rateType: "legal_interest",
          };
          const initial = {
            amounts: [emptyAmount(seed)],
            notes: "",
            asOfDate: null,
          };
          setState(initial);
          lastSentRef.current = null;
          blankRef.current = true;
        }
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [parentId, apiPath, calcAgain, askCalc]);

  // Autosave: 500ms debounce after any state change. Skip the very first
  // render (state still null) and skip when nothing has actually changed.
  useEffect(() => {
    if (!state || !canEdit || blankRef.current) return;
    const serialized = JSON.stringify(state);
    if (serialized === lastSentRef.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        setSaving(true);
        await api.put(apiPath, { state });
        lastSentRef.current = serialized;
      } catch (e: any) {
        console.warn("[calculator] autosave failed:", e?.message);
      } finally {
        setSaving(false);
      }
    }, 500);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [state, apiPath, canEdit]);

  // Live autofill of amount[0] from cs.claimAmount / cs.introductionDate
  // (D6, Phase 15.1). Fires whenever the case's claimAmount or
  // introductionDate change AND amount[0]'s corresponding lock flag is
  // unset. Bypasses patchAmount to avoid arming the lock flag itself.
  // Migration note: pre-Phase-15.1 rows have no lock flags (undefined →
  // falsy), so on first open after deploy they snap to current case
  // values per spec.
  const csClaim = cs?.claimAmount;
  const csIntroISO = toISO(cs?.introductionDate);
  const amount0Id = state?.amounts?.[0]?.id || null;
  useEffect(() => {
    if (csClaim == null && csIntroISO == null) return;
    setState((s: any) => {
      if (!s || !Array.isArray(s.amounts) || !s.amounts.length) return s;
      const a = s.amounts[0];
      let changed = false;
      const next = { ...a };
      if (!a._principalLocked && csClaim != null && a.principal !== csClaim) {
        next.principal = csClaim;
        changed = true;
      }
      if (
        !a._startDateLocked &&
        csIntroISO != null &&
        a.startDate !== csIntroISO
      ) {
        next.startDate = csIntroISO;
        changed = true;
      }
      if (!changed) return s;
      return { ...s, amounts: [next, ...s.amounts.slice(1)] };
    });
  }, [csClaim, csIntroISO, amount0Id]);

  // ----- mutators -------------------------------------------------------
  const mutate = (fn: any) => {
    blankRef.current = false; // 213 §11.5 — a person's change: from now on the calculation is saved
    setState((s: any) => fn(s));
  };

  const addAmount = () =>
    mutate((s: any) => ({ ...s, amounts: [...s.amounts, emptyAmount()] }));
  const removeAmount = (id: any) =>
    mutate((s: any) => ({ ...s, amounts: s.amounts.filter((a: any) => a.id !== id) }));
  // patchAmount: user-driven edit path. When the patch touches amount[0]'s
  // principal or startDate, the corresponding lock flag is set so live
  // autofill stops syncing that field from the case.
  const patchAmount = (id: any, patch: any) =>
    mutate((s: any) => ({
      ...s,
      amounts: s.amounts.map((a: any, idx: any) => {
        if (a.id !== id) return a;
        const next = { ...a, ...patch };
        if (idx === 0) {
          if ("principal" in patch && !("_principalLocked" in patch))
            next._principalLocked = true;
          if ("startDate" in patch && !("_startDateLocked" in patch))
            next._startDateLocked = true;
        }
        return next;
      }),
    }));
  const addPayment = (id: any) =>
    patchAmountAppend(id, "payments", { date: todayISO(), amount: null });
  const addCap = (id: any) =>
    patchAmountAppend(id, "capitalizations", { date: todayISO() });
  const patchAmountAppend = (id: any, key: any, item: any) =>
    mutate((s: any) => ({
      ...s,
      amounts: s.amounts.map((a: any) =>
        a.id === id ? { ...a, [key]: [...(a[key] || []), item] } : a,
      ),
    }));
  const patchSubrow = (id: any, key: any, idx: any, patch: any) =>
    mutate((s: any) => ({
      ...s,
      amounts: s.amounts.map((a: any) => {
        if (a.id !== id) return a;
        const arr = [...(a[key] || [])];
        arr[idx] = { ...arr[idx], ...patch };
        return { ...a, [key]: arr };
      }),
    }));
  const removeSubrow = (id: any, key: any, idx: any) =>
    mutate((s: any) => ({
      ...s,
      amounts: s.amounts.map((a: any) => {
        if (a.id !== id) return a;
        const arr = [...(a[key] || [])];
        arr.splice(idx, 1);
        return { ...a, [key]: arr };
      }),
    }));

  // ----- computation ----------------------------------------------------
  // C.2 — the interest engine moved server-side (POST /api/interest/compute;
  // same arithmetic, verbatim, recording-verified). The section sends the
  // calculator state and displays the returned breakdown — it computes no
  // money. The server loads the official rates itself, in the same order the
  // GET handed the browser engine. Latest-wins: a stale response never
  // overwrites a newer one. The previous result stays on screen until the
  // next response lands ("—" placeholders before the first).
  const [result, setResult] = useState<any>(null);
  const computeKey = state
    ? JSON.stringify({ amounts: state.amounts, asOfDate: state.asOfDate })
    : null;
  useEffect(() => {
    if (!state) {
      setResult(null);
      return;
    }
    let live = true;
    computeInterest({ state })
      .then((r) => {
        if (live) setResult(r);
      })
      .catch(() => {
        if (live) setResult(null);
      });
    return () => {
      live = false;
    };
  }, [computeKey]);

  // ----- actions: memo + clipboard --------------------------------------
  // (The memo filename is built server-side now — P17.9. "Copy summary" below
  // still composes its clipboard text client-side; that is a separate feature.)
  // Exit 26 (brief 38 §9) — the prose composes from the dictionary in the
  // READER's language: it is a copy of the on-screen breakdown, and "a French
  // document" was true only because nobody had built the other languages (the
  // circularity unit C deleted server-side). The shipped French is preserved
  // byte-for-byte as the fr rows — except the rate label, which was RATE_TYPES'
  // ENGLISH fallback inside the French document (the census's named defect) and
  // now resolves properly. Indentation is structural and stays in code; every
  // hole is a formatted number, a date, a resolved label or the user's own
  // text — never a noun the sentence agrees with.
  const composeProse = () => {
    if (!result) return "";
    // Literal keys, deliberately — a `t(`…${k}`)` helper would be one more
    // computed site gate 2 cannot resolve (the §7 saga); written out, gate 2
    // owns these keys' three languages forever.
    const rateLabel = rateTypeLabelUI(t);
    const lines = [];
    const ref = autofill?.ref || cs?.ref || "";
    const title = autofill?.title || cs?.title || "";
    lines.push(
      `${ref ? ref + " — " : ""}${title || t("case.calculator.prose.untitled_case", { fallback: "Case" })} — ${t("case.calculator.prose.title_suffix", { fallback: "Interest calculation" })}`,
    );
    lines.push("");
    lines.push(t("case.calculator.prose.heading_principal", { fallback: "Principal and period" }));
    for (const a of result.perAmount) {
      const src = (state.amounts || []).find((x: any) => x.id === a.id) || {};
      lines.push(
        "• " +
          t("case.calculator.prose.line_amount", {
            label: a.label || t("case.calculator.principal", { fallback: "Principal" }),
            principal: fmtBE(src.principal),
            date: fmtBEDate(src.startDate),
            rate: rateLabel(src.rateType),
            fallback: "{label}: {principal} EUR, as from {date}, rate {rate}.",
          }),
      );
    }
    lines.push("");
    lines.push(t("case.calculator.prose.heading_detail", { fallback: "Calculation detail" }));
    for (const a of result.perAmount) {
      const src = (state.amounts || []).find((x: any) => x.id === a.id) || {};
      lines.push(`— ${a.label || ""}`);
      // c1-rate-coverage (2026-08-04) — this loop never consulted `ok`, so a
      // refused amount printed no periods and then a zero total as fact, with
      // nothing saying it had not computed. Live for every existing refusal,
      // not only a missing rate. Same shape as memoExport and docGen: the
      // reason, and no total. The refusal renders through refusalUI — the
      // reader-language renderer that always existed beside the French one.
      if (!a.result.ok) {
        lines.push(
          `  (${refusalUI(a.result.errorInfo, a.result.error, t, rateLabel)})`,
        );
        continue;
      }
      for (const p of a.result.periods || []) {
        lines.push(
          "  " +
            t("case.calculator.prose.line_period", {
              from: fmtBEDate(p.from),
              to: fmtBEDate(p.to),
              days: p.days,
              rate: fmtBEPercent(p.rate),
              interest: fmtBE(p.interestAccrued),
              fallback: "{from} → {to} ({days} d) — {rate} — interest {interest} EUR",
            }),
        );
        for (const ev of p.events || []) {
          if (ev.kind === "payment") {
            lines.push(
              "    " +
                t("case.calculator.prose.line_payment", {
                  date: fmtBEDate(ev.date),
                  amount: fmtBE(ev.amount),
                  toInterest: fmtBE(ev.allocatedToInterest),
                  toPrincipal: fmtBE(ev.allocatedToPrincipal),
                  fallback:
                    "payment {date} — {amount} EUR (allocated first to interest: {toInterest} EUR; to principal: {toPrincipal} EUR)",
                }),
            );
          } else if (ev.kind === "capitalization") {
            lines.push(
              "    " +
                t("case.calculator.prose.line_capitalization", {
                  date: fmtBEDate(ev.date),
                  rolled: fmtBE(ev.rolled),
                  newPrincipal: fmtBE(ev.newPrincipal),
                  fallback:
                    "capitalization {date} — capitalized interest: {rolled} EUR — new principal: {newPrincipal} EUR",
                }),
            );
          }
        }
      }
      lines.push(
        "  " +
          t("case.calculator.prose.line_totals", {
            interest: fmtBE(a.result.totalInterest),
            principal: fmtBE(a.result.totalPrincipalAtEnd),
            due: fmtBE(a.result.totalDue),
            fallback:
              "Total interest: {interest} EUR — Remaining principal: {principal} EUR — Due: {due} EUR",
          }),
      );
    }
    lines.push("");
    lines.push(t("case.calculator.prose.heading_conclusion", { fallback: "Conclusion" }));
    // c1-rate-coverage — the clipboard prose is pasted straight into a
    // document, so a refused grand must not state a figure here either.
    lines.push(
      result.grand?.ok === false
        ? `(${refusalUI(result.grand.errorInfo, result.grand.error, t, rateLabel)})`
        : t("case.calculator.prose.line_grand", {
            date: fmtBEDate(state.asOfDate || todayISO()),
            due: fmtBE(result.grand.due),
            principal: fmtBE(result.grand.principal),
            interest: fmtBE(result.grand.interest),
            fallback:
              "Total due as of {date}: {due} EUR (principal {principal} EUR + interest {interest} EUR).",
          }),
    );
    if (state.notes) {
      lines.push("");
      lines.push(t("case.calculator.prose.heading_notes", { fallback: "Notes" }));
      lines.push(state.notes);
    }
    return lines.join("\n");
  };

  const onCopyProse = async () => {
    try {
      await navigator.clipboard.writeText(composeProse());
      toast &&
        toast(
          "✓ " +
            t("case.calculator.copied", {
              fallback: "Summary copied to clipboard",
            }),
        );
    } catch (e: any) {
      toast &&
        toast(
          "⚠ " +
            (e?.message ||
              t("case.calculator.copy_failed", { fallback: "copy failed" })),
        );
    }
  };

  const onGenerateMemo = async () => {
    // P17.9 — the memo .docx is now built SERVER-SIDE via the docgen hook
    // (POST /memo assembles + renders from the posted state). We ship the live
    // calculator state; the server recomputes the breakdown and reproduces the
    // former browser memo content-identically. Guard on `result` so the button
    // never fires before the first (server) computation has landed.
    if (busy || !result) return;
    setBusy(true);
    try {
      const out = (await api.post(`${apiPath}/memo`, { state })) as {
        ok?: boolean;
        error?: string;
      } | null;
      if (out?.ok) {
        toast &&
          toast(
            "✓ " +
              t("case.calculator.memo_saved", {
                fallback: "Memo saved to 04.MEMOS",
              }),
          );
      } else {
        toast &&
          toast(
            "⚠ " +
              (out?.error ||
                t("case.calculator.memo_upload_failed", {
                  fallback: "memo upload failed",
                })),
          );
      }
    } catch (e: any) {
      toast &&
        toast(
          "⚠ " +
            (e?.message ||
              t("case.calculator.memo_failed", {
                fallback: "memo generation failed",
              })),
          undefined,
          // 209 §5 [RULED 18] — a case without a language: its form, one click away
          { action: languageRefusalAction((e as { body?: unknown } | null)?.body, String(tImperative("case.language.open_form", { fallback: "Open the case form" }))) },
        );
    } finally {
      setBusy(false);
    }
  };

  // ----- render ---------------------------------------------------------
  if (loading) {
    return (
      <div className="detail-card" style={{ padding: 12 }}>
        <p style={{ fontSize: FS.s11, color: "var(--tm)", margin: 0 }}>
          {t("common.loading", { fallback: "Loading…" })}
        </p>
      </div>
    );
  }
  const tt = t as Translate;
  if (!state)
    return calcRead.failure ? (
      <div className="detail-card" style={{ padding: 12 }} data-read-failed={PAGE_LISTS.calculator}>
        {!hideTitle && (
          <h3 style={{ fontSize: FS.s12, margin: 0 }}>
            {tt("case.calculator.title", { fallback: "Interest calculator" })}
          </h3>
        )}
        <ListLoadContext.Provider value={calcLists}>
          <ListNotLoaded lists={[PAGE_LISTS.calculator]} words={tt("common.failed_to_load", { fallback: "Failed to load" })} />
        </ListLoadContext.Provider>
      </div>
    ) : null;

  // c1-rate-coverage-checkpoint — one condition, read by the grand-total
  // card's TEXT and by its FRAME, so the two cannot disagree.
  const grandRefused = !!(result && result.grand?.ok === false);

  return (
    <div
      className="detail-card"
      style={{ padding: 12, display: "flex", flexDirection: "column", gap: 10 }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        {!hideTitle && (
          <h3 style={{ fontSize: FS.s12, margin: 0 }}>
            {t("case.calculator.title", { fallback: "Interest calculator" })}
          </h3>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          {/* 188 §F.4 [glance correction, Laurent 2026-09-14] — the app's add
              button, like the rest: SharedUI's AddButton (the primary dress,
              its own "+ "), label unchanged. Until 188 this was a ghost button
              with a lucide Plus, which read as bare text. */}
          {canEdit && (
            <AddButton
              label={t("case.calculator.add_amount", { fallback: "Add amount" })}
              onClick={addAmount}
            />
          )}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: 10,
          flexWrap: "nowrap",
          overflowX: "auto",
          paddingBottom: 4,
        }}
      >
        {state.amounts.map((a: any, idx: any) => {
          const r = result?.perAmount.find((x: any) => x.id === a.id)?.result;
          const ratesForKind = ratesByKind(rates, a.rateType);
          return (
            <div key={a.id} style={colStyle}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input
                  className="field-input"
                  style={{ ...inputStyle, fontWeight: 600 }}
                  value={a.label || ""}
                  placeholder={t("case.calculator.amount_label", {
                    fallback: "Label",
                  })}
                  disabled={!canEdit}
                  onChange={(e) => patchAmount(a.id, { label: e.target.value })}
                />
                {canEdit && state.amounts.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-danger"
                    style={{ padding: "2px 4px" }}
                    title={t("common.remove", { fallback: "Remove" })}
                    onClick={() => removeAmount(a.id)}
                  >
                    <Trash2 size={11} />
                  </button>
                )}
              </div>

              <div>
                <label style={labelStyle}>
                  {t("case.calculator.principal", {
                    fallback: "Principal (EUR)",
                  })}
                </label>
                {/* 208 §10 — the one amount field (src/components/AmountField.tsx): typed with a decimal comma, shown « 1 234,50 » */}
                <AmountField
                  style={inputStyle}
                  disabled={!canEdit}
                  value={a.principal}
                  empty=""
                  inputMode="decimal"
                  onAmount={(n) => {
                    patchAmount(a.id, { principal: n });
                  }}
                />
              </div>

              <div style={{ display: "flex", gap: 6 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>
                    {t("case.calculator.start_date", {
                      fallback: "Start date",
                    })}
                  </label>
                  <input
                    className="field-input"
                    type="date"
                    style={inputStyle}
                    disabled={!canEdit}
                    value={a.startDate || ""}
                    onChange={(e) =>
                      patchAmount(a.id, { startDate: e.target.value || null })
                    }
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>
                    {t("case.calculator.end_date", { fallback: "End date" })}
                    <span style={{ color: "var(--tm)", fontStyle: "italic" }}>
                      {" "}
                      {a.endDate
                        ? ""
                        : t("case.calculator.end_today", {
                            fallback: "(today)",
                          })}
                    </span>
                  </label>
                  <input
                    className="field-input"
                    type="date"
                    style={inputStyle}
                    disabled={!canEdit}
                    value={a.endDate || ""}
                    onChange={(e) =>
                      patchAmount(a.id, { endDate: e.target.value || null })
                    }
                  />
                </div>
              </div>

              <div>
                <label style={labelStyle}>
                  {t("case.calculator.rate_type", { fallback: "Rate type" })}
                </label>
                <MenuSelect
                  className="field-input"
                  style={inputStyle}
                  disabled={!canEdit}
                  value={a.rateType}
                  onChange={(v) => patchAmount(a.id, { rateType: v })}
                  options={RATE_TYPES.map(
                    (rt) =>
                      [rt.v, t(rt.labelKey, { fallback: rt.fallback })] as const,
                  )}
                />
                {ratesForKind.length === 0 && (
                  <p
                    style={{
                      fontSize: FS.s9_5,
                      color: "var(--am)",
                      margin: "3px 0 0",
                    }}
                  >
                    {t("case.calculator.no_rates", {
                      fallback: "No rates loaded for this kind.",
                    })}
                  </p>
                )}
              </div>

              {/* Payments */}
              <div>
                <div
                  style={{
                    ...sectionTitleStyle,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <span>
                    {t("case.calculator.payments", {
                      fallback: "Interim payments",
                    })}
                  </span>
                  {canEdit && (
                    <button
                      type="button"
                      className="btn btn-ghost"
                      style={{
                        marginLeft: "auto",
                        padding: "1px 5px",
                        fontSize: FS.s9,
                      }}
                      onClick={() => addPayment(a.id)}
                    >
                      <Plus size={9} />
                    </button>
                  )}
                </div>
                {(a.payments || []).length === 0 && (
                  <p style={{ fontSize: FS.s9_5, color: "var(--tm)", margin: 0 }}>
                    {t("case.calculator.no_payments", {
                      fallback: "No payments",
                    })}
                  </p>
                )}
                {(a.payments || []).map((p: any, i: any) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      gap: 4,
                      alignItems: "center",
                      marginTop: 3,
                    }}
                  >
                    <input
                      className="field-input"
                      type="date"
                      style={{ ...inputStyle, width: "auto", flex: 1 }}
                      disabled={!canEdit}
                      value={p.date || ""}
                      onChange={(e) =>
                        patchSubrow(a.id, "payments", i, {
                          date: e.target.value,
                        })
                      }
                    />
                    {/* 208 §10 — the one amount field (src/components/AmountField.tsx): typed with a decimal comma, shown « 1 234,50 » */}
                    <AmountField
                      style={{ ...inputStyle, width: "auto", flex: 1 }}
                      disabled={!canEdit}
                      value={p.amount}
                      empty=""
                      inputMode="decimal"
                      placeholder="EUR"
                      onAmount={(n) => {
                        patchSubrow(a.id, "payments", i, { amount: n });
                      }}
                    />
                    {canEdit && (
                      <button
                        type="button"
                        className="btn btn-danger"
                        style={{ padding: "2px 4px" }}
                        onClick={() => removeSubrow(a.id, "payments", i)}
                      >
                        <Trash2 size={10} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Capitalizations */}
              <div>
                <div
                  style={{
                    ...sectionTitleStyle,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <span>
                    {t("case.calculator.capitalization", {
                      fallback: "Capitalizations (Art. 1154)",
                    })}
                  </span>
                  <InfoHint
                    title={t("case.calculator.capitalization_tooltip", {
                      fallback:
                        "Art. 1154 CC: annual capitalization requires a judicial demand or an explicit prior agreement and applies only to interest accrued for at least one year. Substantive due-diligence is the user's responsibility — the engine applies the event without enforcing the rule.",
                    })}
                  />
                  {canEdit && (
                    <button
                      type="button"
                      className="btn btn-ghost"
                      style={{
                        marginLeft: "auto",
                        padding: "1px 5px",
                        fontSize: FS.s9,
                      }}
                      onClick={() => addCap(a.id)}
                    >
                      <Plus size={9} />
                    </button>
                  )}
                </div>
                {(a.capitalizations || []).length === 0 && (
                  <p style={{ fontSize: FS.s9_5, color: "var(--tm)", margin: 0 }}>
                    {t("case.calculator.no_caps", {
                      fallback: "No capitalizations",
                    })}
                  </p>
                )}
                {(a.capitalizations || []).map((c: any, i: any) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      gap: 4,
                      alignItems: "center",
                      marginTop: 3,
                    }}
                  >
                    <input
                      className="field-input"
                      type="date"
                      style={{ ...inputStyle, flex: 1 }}
                      disabled={!canEdit}
                      value={c.date || ""}
                      onChange={(e) =>
                        patchSubrow(a.id, "capitalizations", i, {
                          date: e.target.value,
                        })
                      }
                    />
                    {canEdit && (
                      <button
                        type="button"
                        className="btn btn-danger"
                        style={{ padding: "2px 4px" }}
                        onClick={() => removeSubrow(a.id, "capitalizations", i)}
                      >
                        <Trash2 size={10} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Per-period breakdown */}
              <div>
                <div style={sectionTitleStyle}>
                  {t("case.calculator.period_breakdown", {
                    fallback: "Period breakdown",
                  })}
                </div>
                {(!r || !r.ok || (r.periods || []).length === 0) && (
                  <p
                    style={{
                      fontSize: FS.s9_5,
                      color: r && r.error ? "var(--am)" : "var(--tm)",
                      margin: 0,
                    }}
                  >
                    {r && r.error
                      ? refusalUI(r.errorInfo, r.error, t, rateTypeLabelUI(t))
                      : t("case.calculator.no_breakdown", {
                          fallback: "No periods to display yet.",
                        })}
                  </p>
                )}
                {r && r.ok && (r.periods || []).length > 0 && (
                  <div
                    style={{
                      fontSize: FS.s10,
                      fontFamily: "'JetBrains Mono', monospace",
                      color: "var(--ts)",
                      lineHeight: 1.6,
                      marginTop: 3,
                    }}
                  >
                    {r.periods.map((p: any, i: any) => (
                      <div
                        key={i}
                        style={{
                          borderBottom: "1px dotted var(--br)",
                          padding: "2px 0",
                        }}
                      >
                        <div>
                          {fmtBEDate(p.from)} → {fmtBEDate(p.to)} · {p.days}j ·{" "}
                          {fmtBEPercent(p.rate)} · {fmtBE(p.interestAccrued)}{" "}
                          €
                        </div>
                        {(p.events || []).map((ev: any, j: any) => (
                          <div
                            key={j}
                            style={{
                              paddingLeft: 10,
                              color:
                                ev.kind === "capitalization"
                                  ? "var(--am)"
                                  : "var(--gn)",
                            }}
                          >
                            {ev.kind === "payment"
                              ? `${t("case.calculator.payment", { fallback: "Payment" })} ${fmtBEDate(ev.date)} ${fmtBE(ev.amount)} (→ int. ${fmtBE(ev.allocatedToInterest)} / princ. ${fmtBE(ev.allocatedToPrincipal)})`
                              : `${t("case.calculator.capitalization_short", { fallback: "Cap." })} ${fmtBEDate(ev.date)} +${fmtBE(ev.rolled)} → ${fmtBE(ev.newPrincipal)}`}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
                {r && r.ok && (
                  <div
                    style={{
                      marginTop: 6,
                      padding: "4px 6px",
                      background: "rgba(255,255,255,.04)",
                      borderRadius: RAD.r6,
                      fontSize: FS.s10_5,
                    }}
                  >
                    <div>
                      {t("case.calculator.total_interest", {
                        fallback: "Interest",
                      })}
                      : <strong>{fmtBE(r.totalInterest)}</strong>
                    </div>
                    <div>
                      {t("case.calculator.principal_residual", {
                        fallback: "Principal residual",
                      })}
                      : {fmtBE(r.totalPrincipalAtEnd)}
                    </div>
                    <div>
                      {t("case.calculator.total_due", {
                        fallback: "Total due",
                      })}
                      : <strong>{fmtBE(r.totalDue)}</strong> €
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer: three equal-height columns aligned to the calc card grid
          above. Reuses colStyle (same track width, gap, padding, border
          radius) so cell 1 sits under card 1, cell 2 under card 2, cell 3
          under card 3. align-items:stretch keeps them equal-height. */}
      <div
        style={{
          display: "flex",
          gap: 10,
          flexWrap: "nowrap",
          overflowX: "auto",
          paddingBottom: 4,
          alignItems: "stretch",
        }}
      >
        {/* Col 1 — Grand total.

            c1-rate-coverage-checkpoint (brief 30 §3) — THE CARD'S FRAME
            FOLLOWS ITS CONTENT. #258 rendered the refusal in amber and left
            the chrome set unconditionally, so a refusal arrived inside a GREEN
            card: the frame said success and the text said the total cannot be
            stated. Seen on staging in one screenshot; not a logic defect, and
            not something a recording can check.

            The frame switches on the same condition the text does, so the two
            cannot disagree again. The loading state (`result` null, an
            em-dash) deliberately keeps the green: it makes no claim to
            contradict, and a frame that recoloured on every recompute would
            flicker on each keystroke. */}
        <div
          style={{
            ...colStyle,
            background: grandRefused
              ? "color-mix(in srgb, var(--am) 8%, transparent)"
              : "color-mix(in srgb, var(--gn) 8%, transparent)",
            border: `1px solid ${
              grandRefused
                ? "color-mix(in srgb, var(--am) 25%, transparent)"
                : "color-mix(in srgb, var(--gn) 25%, transparent)"
            }`,
          }}
        >
          <div style={sectionTitleStyle}>
            {t("case.calculator.grand_total", { fallback: "Grand total" })}
          </div>
          {/* c1-rate-coverage (2026-08-04) — a refused grand states no figure.
              Same amber the per-amount refusal already uses, so the card that
              is otherwise the confident green bottom line says why instead. */}
          {grandRefused ? (
            <p style={{ fontSize: FS.s10, color: "var(--am)", margin: 0 }}>
              {refusalUI(result.grand.errorInfo, result.grand.error, t, rateTypeLabelUI(t))}
            </p>
          ) : result ? (
            <div style={{ fontSize: FS.s11, lineHeight: 1.6, flex: 1 }}>
              <div>
                {t("case.calculator.grand_principal", {
                  fallback: "Principal",
                })}
                : {fmtBE(result.grand.principal)} €
              </div>
              <div>
                {t("case.calculator.grand_interest", { fallback: "Interest" })}:{" "}
                {fmtBE(result.grand.interest)} €
              </div>
              <div
                style={{
                  marginTop: 4,
                  paddingTop: 4,
                  borderTop: "1px solid color-mix(in srgb, var(--gn) 30%, transparent)",
                }}
              >
                {t("case.calculator.grand_due", { fallback: "Due" })}:{" "}
                <strong>{fmtBE(result.grand.due)} €</strong>
              </div>
            </div>
          ) : (
            <p style={{ fontSize: FS.s10, color: "var(--tm)", margin: 0 }}>—</p>
          )}
        </div>
        {/* Col 2 — Notes */}
        <div style={colStyle}>
          <label style={labelStyle}>
            {t("case.calculator.notes", { fallback: "Notes" })}
          </label>
          <textarea
            className="field-input"
            style={{
              ...inputStyle,
              resize: "vertical",
              flex: 1,
              minHeight: 80,
            }}
            disabled={!canEdit}
            value={state.notes || ""}
            onChange={(e) => mutate((s: any) => ({ ...s, notes: e.target.value }))}
          />
        </div>
        {/* Col 3 — As-of date + Copy summary + Generate memo */}
        <div style={colStyle}>
          <div>
            <label style={labelStyle}>
              {t("case.calculator.as_of_date", { fallback: "As-of date" })}
            </label>
            <input
              className="field-input"
              type="date"
              style={inputStyle}
              disabled={!canEdit}
              value={state.asOfDate || ""}
              onChange={(e) =>
                mutate((s: any) => ({ ...s, asOfDate: e.target.value || null }))
              }
            />
            <p style={{ fontSize: FS.s9_5, color: "var(--tm)", margin: "3px 0 0" }}>
              {t("case.calculator.as_of_hint", { fallback: "Blank = today." })}
            </p>
          </div>
          <button type="button" className="btn-unified btn-unified-secondary" onClick={onCopyProse}>
            <Copy size={12} />{" "}
            {t("case.calculator.copy_summary", { fallback: "Copy summary" })}
          </button>
          {canEdit && (
            <button
              type="button"
              className="btn-unified btn-unified-primary"
              disabled={busy}
              onClick={onGenerateMemo}
            >
              <FileText size={12} />{" "}
              {t("case.calculator.generate_memo", {
                fallback: "Generate memo",
              })}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
